import ReceptionTime, {IReceptionTime} from "@/types/clients/esquire/data/general/reception-time";
import BuildingImage, {BuildingImageData} from "@/types/clients/esquire/data/general/building-image";
import BuildingDetail, {IBuildingDetail} from "@/types/clients/esquire/data/general/building-detail";

export interface IBuilding {
  id: string;
  name: string;
  address: string;
  zip: string;
  city: string;
  public_phone_number: string;
  emergency_phone_number: string;
  reception_times: IReceptionTime | null;
  from: string;
  until: string;
  requires_online_checkin: boolean;
  building_image: BuildingImageData | null;
  details?: IBuildingDetail[] | null;


  gmaps_place_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export default class Building {
  public id: string;
  public name: string;
  public address: string;
  public zip: string;
  public city: string;
  public publicPhoneNumber: string;
  public emergencyPhoneNumber: string;
  public receptionTimes: ReceptionTime | null;
  public from: string;
  public until: string;
  public requiresOnlineCheckin: boolean;
  public buildingImage: BuildingImage | null;
  public details: BuildingDetail[] | null;

  public gmapsPlaceId: string | null;
  public latitude: number | null;
  public longitude: number | null;

  constructor(data: IBuilding) {
    this.id = data.id;
    this.name = data.name;
    this.address = data.address;
    this.zip = data.zip;
    this.city = data.city;
    this.publicPhoneNumber = data.public_phone_number;
    this.emergencyPhoneNumber = data.emergency_phone_number;
    this.requiresOnlineCheckin = data.requires_online_checkin ?? false;

    this.gmapsPlaceId = data.gmaps_place_id ?? null;
    this.latitude = typeof data.latitude === "number" ? data.latitude : null;
    this.longitude = typeof data.longitude === "number" ? data.longitude : null;


    if (data.reception_times) {
      this.receptionTimes = new ReceptionTime(data.reception_times as IReceptionTime);
      this.from = this.receptionTimes.receptionCheckInFrom();
      this.until = this.receptionTimes.receptionCheckInUntil();
    } else {
      this.receptionTimes = null;
      this.from = "";
      this.until = "";
    }

    this.buildingImage = data.building_image ? new BuildingImage(data.building_image) : null;
    this.details = Array.isArray(data.details)
      ? data.details.map((detail: IBuildingDetail) => new BuildingDetail(detail))
      : null;
  }

  public get directionsUrl(): string {
    const base = "https://www.google.com/maps/dir/?api=1";

    const placeId = this.gmapsPlaceId?.trim() ?? "";
    const fullAddress = [this.address, `${this.zip} ${this.city}`].filter(Boolean).join(", ").trim();
    const destinationFallback = encodeURIComponent(fullAddress || this.name || "");

    if (placeId) {
      const qp = [
        `destination_place_id=${encodeURIComponent(placeId)}`,
        destinationFallback ? `destination=${destinationFallback}` : "",
        "travelmode=driving"
      ].filter(Boolean).join("&");

      return `${base}&${qp}`;
    }

    const hasCoords = typeof this.latitude === "number" && typeof this.longitude === "number";
    if (hasCoords) {
      return `${base}&destination=${this.latitude},${this.longitude}&travelmode=driving`;
    }

    const parts = [this.name, this.address, this.zip, this.city].filter((v) => (v ?? "").trim().length);
    const query = encodeURIComponent(parts.join(", "));
    return `${base}&destination=${query}&travelmode=driving`;
  }

  public getLaundryLocationIfPresent(locale: string): string | null {
    const normalizedLocale = locale.trim().toLowerCase();

    const exactMatch = this.details?.find((detail: BuildingDetail) => {
      return detail.language?.trim().toLowerCase() === normalizedLocale;
    });

    const fallbackMatch = this.details?.find((detail: BuildingDetail) => {
      return detail.language?.trim().toLowerCase() === "en";
    });

    return exactMatch?.laundryLocation ?? fallbackMatch?.laundryLocation ?? null;
  }

  toDTO(): IBuilding {
    return {
      id: this.id,
      name: this.name,
      address: this.address,
      zip: this.zip,
      city: this.city,
      public_phone_number: this.publicPhoneNumber,
      emergency_phone_number: this.emergencyPhoneNumber,
      reception_times: this.receptionTimes ? this.receptionTimes.toDTO(): null,
      from: this.from,
      until: this.until,
      requires_online_checkin: this.requiresOnlineCheckin,
      building_image: this.buildingImage ? this.buildingImage.toJson() as BuildingImageData : null,
      gmaps_place_id: this.gmapsPlaceId,
      latitude: this.latitude,
      longitude: this.longitude,
      details: this.details?.map((detail: BuildingDetail) => detail.toDTO()) ?? null
    };
  }

}

